import { PaneChannelTiming } from './AnimationTimingTypes';
import { ArrayLikeUtils } from './ArrayLikeUtils';
import { DataStorePaths } from './DataStorePaths';
import { IDataStore } from './IDataStore';
import { PaneStateAttribute, PaneStateAttributeUtil } from './PaneStateAttribute';

/** Animation fraction constants */
const ANIMATION_FRACTION_START = 0.0;
const ANIMATION_FRACTION_END = 1.0;

/**
 * Type for datastore values.
 * Uses unknown for flexibility with proper type guards where needed.
 * - 'unknown' forces type checking before use
 * - DO NOT USE 'any' as it bypasses all type checking (unsafe)
 *
 * Type checking happens in:
 * - ArrayLikeUtils.toArray() - handles arrays/TypedArrays safely
 * - Runtime typeof checks - e.g., if (typeof startValue === 'number' && typeof endValue === 'number')
 * - Length checks - e.g., if (startArray.length > 0 && startArray.length === endArray.length)
 */
export type DataStoreValue = unknown;

// ──────────────────────────────────────────────────────────────────────────────
// Constants
// ──────────────────────────────────────────────────────────────────────────────

/** Full circle in degrees */
const FULL_CIRCLE_DEGREES = 360;

/** Half circle in degrees (for shortest path calculation) */
const HALF_CIRCLE_DEGREES = 180;

/** ARGB bit shift amounts */
const ALPHA_SHIFT = 24;
const RED_SHIFT = 16;
const GREEN_SHIFT = 8;
const BLUE_SHIFT = 0;

/** ARGB channel mask (8 bits per channel) */
const CHANNEL_MASK = 0xff;

/** RGB mask (preserves RGB, clears alpha) */
const RGB_MASK = 0x00ffffff;

// ──────────────────────────────────────────────────────────────────────────────
// Interpolation helpers
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Linear interpolation.
 *
 * @param start - Start value
 * @param end - End value
 * @param fraction - Interpolation fraction [0, 1]
 * @returns Interpolated value
 */
export function lerp(start: number, end: number, fraction: number): number {
  return start + (end - start) * fraction;
}

/**
 * Color interpolation (per-channel ARGB).
 *
 * Colors are stored as 32-bit ARGB integers (0xAARRGGBB).
 * We interpolate each channel (A, R, G, B) independently.
 *
 * @param startColor - Start color (ARGB integer)
 * @param endColor - End color (ARGB integer)
 * @param fraction - Interpolation fraction [0, 1]
 * @returns Interpolated color (ARGB integer)
 */
export function interpolateColor(startColor: number, endColor: number, fraction: number): number {
  // Extract ARGB channels from start color
  const startA = (startColor >> ALPHA_SHIFT) & CHANNEL_MASK;
  const startR = (startColor >> RED_SHIFT) & CHANNEL_MASK;
  const startG = (startColor >> GREEN_SHIFT) & CHANNEL_MASK;
  const startB = (startColor >> BLUE_SHIFT) & CHANNEL_MASK;

  // Extract ARGB channels from end color
  const endA = (endColor >> ALPHA_SHIFT) & CHANNEL_MASK;
  const endR = (endColor >> RED_SHIFT) & CHANNEL_MASK;
  const endG = (endColor >> GREEN_SHIFT) & CHANNEL_MASK;
  const endB = (endColor >> BLUE_SHIFT) & CHANNEL_MASK;

  // Interpolate each channel independently
  const interpA = Math.round(lerp(startA, endA, fraction));
  const interpR = Math.round(lerp(startR, endR, fraction));
  const interpG = Math.round(lerp(startG, endG, fraction));
  const interpB = Math.round(lerp(startB, endB, fraction));

  // Pack back into 32-bit integer
  // Use >>> 0 to ensure unsigned 32-bit integer
  return ((interpA << ALPHA_SHIFT) | (interpR << RED_SHIFT) | (interpG << GREEN_SHIFT) | (interpB << BLUE_SHIFT)) >>> 0;
}

/**
 * Angle interpolation with 360° wrapping.
 *
 * Always takes the shortest angular path.
 * Example: 350° → 10° goes through 0° (20° forward), not through 180° (340° backward)
 *
 * @param startAngle - Start angle in degrees
 * @param endAngle - End angle in degrees
 * @param fraction - Interpolation fraction [0, 1]
 * @returns Interpolated angle in degrees [0, 360)
 */
export function interpolateAngle(startAngle: number, endAngle: number, fraction: number): number {
  // Normalize both angles to [0, FULL_CIRCLE_DEGREES)
  const startNorm = ((startAngle % FULL_CIRCLE_DEGREES) + FULL_CIRCLE_DEGREES) % FULL_CIRCLE_DEGREES;
  const endNorm = ((endAngle % FULL_CIRCLE_DEGREES) + FULL_CIRCLE_DEGREES) % FULL_CIRCLE_DEGREES;

  // Calculate shortest angular distance
  let diff = endNorm - startNorm;
  if (diff > HALF_CIRCLE_DEGREES) {
    diff -= FULL_CIRCLE_DEGREES;
  } else if (diff < -HALF_CIRCLE_DEGREES) {
    diff += FULL_CIRCLE_DEGREES;
  }

  // Interpolate and normalize result
  const result = startNorm + diff * fraction;
  return ((result % FULL_CIRCLE_DEGREES) + FULL_CIRCLE_DEGREES) % FULL_CIRCLE_DEGREES;
}

/**
 * Helper to make a color transparent by setting alpha to 0 while preserving RGB.
 */
function makeColorTransparent(color: number): number {
  return (color & RGB_MASK) >>> 0;
}

/**
 * Common logic for mark animation values based on attribute type.
 */
function getMarkAnimationValue(value: number, attribute: PaneStateAttribute): number {
  if (PaneStateAttributeUtil.isColorAttribute(attribute)) {
    return makeColorTransparent(value);
  } else if (PaneStateAttributeUtil.isWeightAttribute(attribute)) {
    return 0;
  } else {
    // For non-color/weight attributes (e.g. size), use original value (no animation)
    return value;
  }
}

/**
 * Compute start value for an entering mark (fade in effect).
 * C++ Reference: CreateStartStateEnterFadeOn()
 * Entering marks start from transparent (alpha=0) for color, 0 for weight, TO value for others.
 */
export function getEnteringMarkStartValue(endVal: number, attribute: PaneStateAttribute): number {
  return getMarkAnimationValue(endVal, attribute);
}

/**
 * Compute end value for an exiting mark (fade out effect).
 * Exiting marks fade to transparent (alpha=0) for color, 0 for weight, FROM value for others.
 */
export function getExitingMarkEndValue(startVal: number, attribute: PaneStateAttribute): number {
  return getMarkAnimationValue(startVal, attribute);
}

// ──────────────────────────────────────────────────────────────────────────────
// PageIntervalTable types and helpers (used by exit-mark injection)
// ──────────────────────────────────────────────────────────────────────────────

export interface PageIntervalTableData {
  startRaw: DataStoreValue;
  endRaw: DataStoreValue;
  start: number[];
  end: number[];
  paneKey: string[];
  paneCol: number[];
  paneRow: number[];
  paneLayer: number[];
  panePage: number[];
}

/**
 * Read and validate all PageIntervalTable columns from a datastore.
 * Returns null if any required column is missing or columns have mismatched lengths.
 */
export function readPageIntervalTable(ds: IDataStore): PageIntervalTableData | null {
  const fields = DataStorePaths.PAGE_INTERVAL_FIELDS;
  const rawValues: DataStoreValue[] = [];
  for (const field of fields) {
    const val = ds.getValue(field) as DataStoreValue;
    if (val == null) {
      return null;
    }
    rawValues.push(val);
  }

  const start = ArrayLikeUtils.toArray<number>(rawValues[0]);
  const end = ArrayLikeUtils.toArray<number>(rawValues[1]);
  const paneKey = ArrayLikeUtils.toArray<string>(rawValues[2]);
  const paneCol = ArrayLikeUtils.toArray<number>(rawValues[3]);
  const paneRow = ArrayLikeUtils.toArray<number>(rawValues[4]);
  const paneLayer = ArrayLikeUtils.toArray<number>(rawValues[5]);
  const panePage = ArrayLikeUtils.toArray<number>(rawValues[6]);

  const len = start.length;
  if (
    len === 0 ||
    end.length !== len ||
    paneKey.length !== len ||
    paneCol.length !== len ||
    paneRow.length !== len ||
    paneLayer.length !== len ||
    panePage.length !== len
  ) {
    return null;
  }

  return {
    startRaw: rawValues[0],
    endRaw: rawValues[1],
    start,
    end,
    paneKey,
    paneCol,
    paneRow,
    paneLayer,
    panePage,
  };
}

/**
 * Compute a composite identity string for a sub-pane from its col/row/layer/page.
 * Used to match sub-panes across FROM and TO states.
 */
export function subPaneIdentity(col: unknown, row: unknown, layer: unknown, page: unknown): string {
  return [col, row, layer, page].join(':');
}

/**
 * Build a map from sub-pane identity → interval table row index,
 * filtering to rows that belong to the given paneId.
 */
export function buildSubPaneIdentityMap(
  paneId: string,
  table: {
    start: number[];
    paneKey: string[];
    paneCol: number[];
    paneRow: number[];
    paneLayer: number[];
    panePage: number[];
  }
): Map<string, number> {
  const map = new Map<string, number>();
  for (let i = 0; i < table.start.length; i++) {
    if (table.paneKey[i] !== paneId) {
      continue;
    }
    const identity = subPaneIdentity(table.paneCol[i], table.paneRow[i], table.paneLayer[i], table.panePage[i]);
    map.set(identity, i);
  }
  return map;
}

/**
 * Find which interval table row a mark index falls into (by start/end range),
 * filtered to rows belonging to the given paneId.
 * Returns -1 if no matching row is found.
 */
export function findIntervalRowForIndex(
  paneId: string,
  markIndex: number,
  table: { start: number[]; end: number[]; paneKey: string[] }
): number {
  for (let i = 0; i < table.start.length; i++) {
    if (table.paneKey[i] !== paneId) {
      continue;
    }
    if (table.start[i] <= markIndex && markIndex < table.end[i]) {
      return i;
    }
  }
  return -1;
}

/**
 * Group exiting FROM mark indices by their sub-pane identity in the FROM state.
 * Returns Map<subPaneIdentity, fromMarkIndices[]>.
 */
export function groupExitingMarksBySubPane(
  paneId: string,
  exitingFromIndices: number[],
  fromTable: {
    start: number[];
    end: number[];
    paneKey: string[];
    paneCol: number[];
    paneRow: number[];
    paneLayer: number[];
    panePage: number[];
  }
): Map<string, number[]> {
  const grouped = new Map<string, number[]>();
  for (const fromIdx of exitingFromIndices) {
    const row = findIntervalRowForIndex(paneId, fromIdx, fromTable);
    if (row < 0) {
      continue;
    }
    const identity = subPaneIdentity(fromTable.paneCol[row], fromTable.paneRow[row], fromTable.paneLayer[row], fromTable.panePage[row]);
    if (!grouped.has(identity)) {
      grouped.set(identity, []);
    }
    grouped.get(identity)!.push(fromIdx);
  }
  return grouped;
}

/**
 * Build insertion operations that pair each group of exiting marks
 * (by sub-pane identity) with its insertion point in the TO state.
 */
export function buildInsertionOps(
  exitsBySubPane: Map<string, number[]>,
  toSubPaneToRow: Map<string, number>,
  toEnd: number[]
): Array<{ toTableRow: number; insertAt: number; exitFromIndices: number[] }> {
  const insertionOperations: Array<{
    toTableRow: number;
    insertAt: number;
    exitFromIndices: number[];
  }> = [];
  exitsBySubPane.forEach((exitFromIndices: number[], identity: string) => {
    const toRow = toSubPaneToRow.get(identity);
    if (toRow === undefined) {
      return;
    }
    const insertAt = toEnd[toRow];
    if (typeof insertAt !== 'number') {
      return;
    }
    insertionOperations.push({ toTableRow: toRow, insertAt, exitFromIndices });
  });
  return insertionOperations;
}

/**
 * Splice values from the FROM encoding array into the TO encoding array
 * at the specified insertion index. Handles both TypedArrays and plain arrays.
 */
export function spliceExitValuesIntoEncoding(
  path: string,
  fromDS: IDataStore,
  toDS: IDataStore,
  exitFromIndices: number[],
  insertAt: number
): void {
  const toValue = toDS.getValue(path) as DataStoreValue;
  const fromValue = fromDS.getValue(path) as DataStoreValue;
  if (toValue == null || fromValue == null) {
    return;
  }

  const toArr = ArrayLikeUtils.toArray<unknown>(toValue);
  const fromArr = ArrayLikeUtils.toArray<unknown>(fromValue);
  if (toArr.length === 0 || fromArr.length === 0) {
    return;
  }

  // Pick values at the exiting indices from the FROM array
  const valuesToInsert: unknown[] = [];
  for (const idx of exitFromIndices) {
    if (idx < fromArr.length) {
      valuesToInsert.push(fromArr[idx]);
    }
  }
  if (valuesToInsert.length === 0) {
    return;
  }

  // Splice into the TO array
  const merged = [...toArr.slice(0, insertAt), ...valuesToInsert, ...toArr.slice(insertAt)];

  // Preserve TypedArray type if the original was a TypedArray
  toDS.setValue(path, preserveArrayType(toValue, merged as number[]));
}

/**
 * After inserting `count` marks at `insertAt`, shift the PageIntervalTable
 * start/end indices for the target sub-pane and all subsequent sub-panes.
 */
export function shiftIntervalIndices(
  table: { start: number[]; end: number[]; paneKey: string[] },
  paneId: string,
  insertedRow: number,
  insertAt: number,
  count: number
): void {
  for (let rowIndex = 0; rowIndex < table.start.length; rowIndex++) {
    if (table.paneKey[rowIndex] !== paneId) {
      continue;
    }
    if (rowIndex === insertedRow) {
      // Expand the sub-pane that received the inserted marks
      table.end[rowIndex] = table.end[rowIndex] + count;
    } else if (table.start[rowIndex] >= insertAt) {
      // Shift subsequent sub-panes forward
      table.start[rowIndex] = table.start[rowIndex] + count;
      table.end[rowIndex] = table.end[rowIndex] + count;
    }
  }
}

/**
 * Write the mutated start/end arrays back to the datastore,
 * preserving the original TypedArray type if applicable.
 */
export function writeBackIntervalTable(
  ds: IDataStore,
  table: { startRaw: DataStoreValue; endRaw: DataStoreValue; start: number[]; end: number[] }
): void {
  ds.setValue(DataStorePaths.PAGE_INTERVAL_START, preserveArrayType(table.startRaw, table.start));
  ds.setValue(DataStorePaths.PAGE_INTERVAL_END, preserveArrayType(table.endRaw, table.end));
}

/**
 * Find indices of marks that exist in FROM but not in TO (exiting marks).
 */
export function findExitingMarkIndices(fromMarkIds: number[], toMarkIds: number[]): number[] {
  const toMarkIdSet = new Set(toMarkIds);
  const exitingFromIndices: number[] = [];
  for (let i = 0; i < fromMarkIds.length; i++) {
    if (!toMarkIdSet.has(fromMarkIds[i])) {
      exitingFromIndices.push(i);
    }
  }
  return exitingFromIndices;
}

/**
 * Execute sorted insertion operations: splice exiting mark values into TO encoding arrays,
 * shift PageIntervalTable indices, and collect the resulting TO indices.
 */
export function executeExitMarkInsertions(
  insertions: Array<{ toTableRow: number; insertAt: number; exitFromIndices: number[] }>,
  encodingsPath: string,
  fromDS: IDataStore,
  toDS: IDataStore,
  toTable: PageIntervalTableData,
  paneId: string
): Set<number> {
  const exitingToIndices = new Set<number>();
  const encodingKeys = toDS.getAllKeys(encodingsPath);

  for (const insertion of insertions) {
    const count = insertion.exitFromIndices.length;
    if (count === 0) {
      continue;
    }
    const insertAt = toTable.end[insertion.toTableRow];
    if (typeof insertAt !== 'number') {
      continue;
    }

    // Splice exiting mark values into each encoding column
    for (const key of encodingKeys) {
      const path = `${encodingsPath}.${key}`;
      spliceExitValuesIntoEncoding(path, fromDS, toDS, insertion.exitFromIndices, insertAt);
    }

    // Shift PageIntervalTable start/end indices for subsequent sub-panes
    shiftIntervalIndices(toTable, paneId, insertion.toTableRow, insertAt, count);

    // Record the TO indices where exit marks were placed
    for (let offset = 0; offset < count; offset++) {
      exitingToIndices.add(insertAt + offset);
    }
  }
  return exitingToIndices;
}

/**
 * Helper to preserve TypedArray type when setting values
 */
export function preserveArrayType(originalValue: DataStoreValue, newArray: number[]): DataStoreValue {
  if (ArrayBuffer.isView(originalValue)) {
    return ArrayLikeUtils.preserveType(originalValue, newArray);
  }
  return newArray;
}

/**
 * Detect if this datastore represents a map visualization.
 *
 * A map scenario is identified by non-zero map domain bounds.
 * Map domain bounds define the geographic extent (lat/long) of the visualization.
 * If any of these bounds are non-zero, we treat it as a map to enable map-specific
 * animations like enter/exit fade effects.
 *
 * This mirrors the C++ DataStorePaneStateScenarioAnalyzer logic which checks
 * MapDomainLeft/Right for X-axis and MapDomainBottom/Top for Y-axis.
 *
 * @param ds - The datastore to check
 * @returns true if this appears to be a map visualization
 */
export function isMapVizType(ds: IDataStore): boolean {
  return (
    hasNonZeroMapDomain(ds, DataStorePaths.MAP_DOMAIN_LEFT) ||
    hasNonZeroMapDomain(ds, DataStorePaths.MAP_DOMAIN_RIGHT) ||
    hasNonZeroMapDomain(ds, DataStorePaths.MAP_DOMAIN_BOTTOM) ||
    hasNonZeroMapDomain(ds, DataStorePaths.MAP_DOMAIN_TOP)
  );
}

/**
 * Helper to check if a map domain array has any non-zero values.
 * Mirrors C++ MapDomainLeft/Right/Bottom/Top implementation pattern.
 *
 * @param ds - The datastore to check
 * @param path - The map domain path (e.g., 'Viewpoint.PerPaneExtents.MapDomainLeft')
 * @returns true if any value in the array is non-zero
 */
function hasNonZeroMapDomain(ds: IDataStore, path: string): boolean {
  const value = ds.getValue(path) as DataStoreValue;
  if (value === undefined || value === null) {
    return false; // Missing domain bounds - not a map
  }

  // Convert to array for iteration (handles TypedArray, Array, etc.)
  const bounds = ArrayLikeUtils.toArray(value);

  // Check if any bound value is non-zero
  for (const bound of bounds) {
    if (typeof bound === 'number' && bound !== 0) {
      return true;
    }
  }

  return false;
}

// ──────────────────────────────────────────────────────────────────────────────
// Map interpolation utilities
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Build reverse mapping from TO index to FROM index.
 */
export function buildReverseMapping(indexMapping: Map<number, number>): Map<number, number> {
  const reverseMapping = new Map<number, number>();
  for (const [fromIdx, toIdx] of Array.from(indexMapping.entries())) {
    reverseMapping.set(toIdx, fromIdx);
  }
  return reverseMapping;
}

/**
 * Get interpolation data (start and end values) for a specific TO index.
 */
export function getInterpolationData(
  startArray: number[],
  endVal: number,
  toIndex: number,
  attribute: PaneStateAttribute,
  reverseMapping: Map<number, number>,
  enteringToIndices: Set<number>,
  exitingToIndices: Set<number> | undefined
): { startVal: number; actualEndVal: number } {
  if (enteringToIndices.has(toIndex)) {
    return {
      startVal: getEnteringMarkStartValue(endVal, attribute),
      actualEndVal: endVal,
    };
  }

  const fromIndex = reverseMapping.get(toIndex) ?? toIndex;
  const startVal = fromIndex < startArray.length ? startArray[fromIndex] : endVal;

  if (exitingToIndices?.has(toIndex)) {
    return {
      startVal,
      actualEndVal: getExitingMarkEndValue(startVal, attribute),
    };
  }

  return { startVal, actualEndVal: endVal };
}

/**
 * Compare two individual element values for near-equality.
 * Color attributes use exact equality; others use epsilon tolerance.
 */
export function elementsNearlyEqual(leftVal: number, rightVal: number, attribute: PaneStateAttribute, epsilon: number): boolean {
  if (PaneStateAttributeUtil.isColorAttribute(attribute)) {
    return leftVal === rightVal;
  }
  return Math.abs(leftVal - rightVal) <= epsilon;
}

/**
 * Compare two datastore values for near-equality, with optional index mappings.
 * Supports indexed comparison, full-array comparison, and scalar comparison.
 */
export function valuesNearlyEqual(
  left: DataStoreValue,
  right: DataStoreValue,
  attribute: PaneStateAttribute,
  epsilon: number,
  leftIndices?: number[] | null,
  rightIndices?: number[] | null
): boolean {
  if ((left === undefined || left === null) && (right === undefined || right === null)) {
    return true;
  }
  if (left === undefined || left === null || right === undefined || right === null) {
    return false;
  }

  const leftArray = ArrayLikeUtils.toArray(left);
  const rightArray = ArrayLikeUtils.toArray(right);

  // Indexed comparison: compare only elements at specified indices
  if ((leftIndices != null || rightIndices != null) && (leftArray.length > 0 || rightArray.length > 0)) {
    const resolvedLeft = leftIndices ?? [];
    const resolvedRight = rightIndices ?? [];
    const count = Math.min(resolvedLeft.length, resolvedRight.length, leftArray.length, rightArray.length);
    if (count === 0) {
      return true;
    }
    for (let i = 0; i < count; i++) {
      const li = resolvedLeft[i];
      const ri = resolvedRight[i];
      if (li === -1 && ri === -1) continue;
      if (li === -1 || ri === -1) return false;
      if (li < 0 || ri < 0 || li >= leftArray.length || ri >= rightArray.length) return false;
      const lv = leftArray[li];
      const rv = rightArray[ri];
      if (typeof lv === 'number' && typeof rv === 'number') {
        if (!elementsNearlyEqual(lv, rv, attribute, epsilon)) return false;
      } else if (lv !== rv) {
        return false;
      }
    }
    return true;
  }

  // Full-array comparison
  if (leftArray.length > 0 || rightArray.length > 0) {
    if (leftArray.length !== rightArray.length) return false;
    for (let i = 0; i < leftArray.length; i++) {
      const lv = leftArray[i];
      const rv = rightArray[i];
      if (typeof lv === 'number' && typeof rv === 'number') {
        if (!elementsNearlyEqual(lv, rv, attribute, epsilon)) return false;
      } else if (lv !== rv) {
        return false;
      }
    }
    return true;
  }

  // Scalar comparison
  if (typeof left === 'number' && typeof right === 'number') {
    return elementsNearlyEqual(left, right, attribute, epsilon);
  }
  return left === right;
}

/**
 * Interpolate a single value based on attribute type.
 */
export function interpolateValue(startVal: number, endVal: number, fraction: number, attribute: PaneStateAttribute): number {
  if (PaneStateAttributeUtil.isColorAttribute(attribute)) {
    return interpolateColor(startVal, endVal, fraction);
  } else if (PaneStateAttributeUtil.isAngleAttribute(attribute)) {
    return interpolateAngle(startVal, endVal, fraction);
  }
  return lerp(startVal, endVal, fraction);
}

// ──────────────────────────────────────────────────────────────────────────────
// Animation timing helpers
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Derive a local sub-fraction for one channel from the overall animation fraction.
 * C++ Reference: FractionWarperAnimation with delay/duration timing window.
 */
function deriveFraction(globalFraction: number, delay: number, duration: number): number {
  if (duration <= 0) {
    return ANIMATION_FRACTION_END; // has no marks — snap to end
  }
  if (globalFraction <= delay) {
    return ANIMATION_FRACTION_START;
  }
  if (globalFraction >= delay + duration) {
    return ANIMATION_FRACTION_END;
  }
  return (globalFraction - delay) / duration;
}

/**
 * Derive a warped fraction using timing and optional warping function.
 */
export function deriveWarpedFraction(globalFraction: number, timing: PaneChannelTiming): number {
  const localFraction = deriveFraction(globalFraction, timing.delay, timing.duration);
  return timing.warpingFunction !== null ? timing.warpingFunction(localFraction) : localFraction;
}

/**
 * Get attribute timing with fallback to overall timing.
 */
export function getAttributeTiming(
  attribute: PaneStateAttribute,
  overallTiming: PaneChannelTiming,
  overrideTimings: Map<PaneStateAttribute, PaneChannelTiming>
): PaneChannelTiming {
  return overrideTimings.get(attribute) ?? overallTiming;
}

// ──────────────────────────────────────────────────────────────────────────────
// Mark and pane index utilities
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Build a mapping from mark IDs to their indices for efficient lookup.
 */
function buildMarkIdToIndexMap(markIds: number[]): Map<number, number> {
  const markIdToIndex = new Map<number, number>();
  for (let i = 0; i < markIds.length; i++) {
    const markId = markIds[i];
    if (typeof markId === 'number' && !markIdToIndex.has(markId)) {
      markIdToIndex.set(markId, i);
    }
  }
  return markIdToIndex;
}

/**
 * Get mark indices for a set of mark IDs in a specific pane.
 */
export function getMarkIndicesForPane(getValue: (path: string) => DataStoreValue, paneId: string, markIds: number[]): number[] {
  const paneMarkIds = ArrayLikeUtils.toArray<number>(getValue(`PDMarks.${paneId}.Encodings.MarkId`));
  const markIdToIndex = buildMarkIdToIndexMap(paneMarkIds);
  return markIds.map((markId) => markIdToIndex.get(markId) ?? -1);
}

/**
 * Get label indices for a set of mark IDs in a specific pane.
 */
export function getLabelIndicesForPane(
  getValue: (path: string) => DataStoreValue,
  paneId: string,
  markIds: number[],
  getLabelMarkIdsForPaneFunc: (paneId: string) => number[]
): number[] {
  const labelMarkIds = getLabelMarkIdsForPaneFunc(paneId);
  const markIdToIndex = buildMarkIdToIndexMap(labelMarkIds);
  return markIds.map((markId) => markIdToIndex.get(markId) ?? -1);
}

/**
 * Get pane indices for mark IDs in a specific pane using pane index mapping.
 */
export function getPaneIndicesForMarkIdsInPane(
  paneId: string,
  markIds: number[],
  buildPaneIdMarkIdToPaneIndexMap: (paneId: string, outMap: Map<string, number>) => void
): number[] {
  const paneIndexMap = new Map<string, number>();
  buildPaneIdMarkIdToPaneIndexMap(paneId, paneIndexMap);
  return markIds.map((markId) => paneIndexMap.get(`${paneId}:${markId}`) ?? -1);
}

/**
 * Get X grid line indices for mark IDs in a pane.
 */
export function getXGridLineIndicesForMarkIdsInPane(
  getValue: (path: string) => DataStoreValue,
  paneId: string,
  markIds: number[],
  getMarkIndicesForPaneFunc: (paneId: string, markIds: number[]) => number[]
): number[] {
  const paneRows = ArrayLikeUtils.toArray<number>(getValue(`PDMarks.${paneId}.Encodings.pane_row`));
  const paneCols = ArrayLikeUtils.toArray<number>(getValue(`PDMarks.${paneId}.Encodings.pane_col`));
  const markIndices = getMarkIndicesForPaneFunc(paneId, markIds);
  return markIndices.map((markIndex) => {
    if (markIndex < 0 || markIndex >= paneRows.length || markIndex >= paneCols.length) {
      return -1;
    }
    const paneCol = paneCols[markIndex];
    return typeof paneCol === 'number' ? paneCol : -1;
  });
}

/**
 * Get Y grid line indices for mark IDs in a pane.
 */
export function getYGridLineIndicesForMarkIdsInPane(
  getValue: (path: string) => DataStoreValue,
  paneId: string,
  markIds: number[],
  getMarkIndicesForPaneFunc: (paneId: string, markIds: number[]) => number[],
  getNumPaneColumns: () => number
): number[] {
  const paneRows = ArrayLikeUtils.toArray<number>(getValue(`PDMarks.${paneId}.Encodings.pane_row`));
  const paneCols = ArrayLikeUtils.toArray<number>(getValue(`PDMarks.${paneId}.Encodings.pane_col`));
  const columnCount = getNumPaneColumns();
  const markIndices = getMarkIndicesForPaneFunc(paneId, markIds);
  return markIndices.map((markIndex) => {
    if (markIndex < 0 || markIndex >= paneRows.length || markIndex >= paneCols.length) {
      return -1;
    }
    const paneRow = paneRows[markIndex];
    return typeof paneRow === 'number' ? columnCount + paneRow : -1;
  });
}

/**
 * Get user pane row indices for mark IDs in a pane.
 */
export function getUserPaneRowIndicesForMarkIdsInPane(
  getValue: (path: string) => DataStoreValue,
  paneId: string,
  markIds: number[],
  getMarkIndicesForPaneFunc: (paneId: string, markIds: number[]) => number[]
): number[] {
  const userPaneRows = ArrayLikeUtils.toArray<number>(getValue(`PDMarks.${paneId}.Encodings.user_pane_row`));
  const markIndices = getMarkIndicesForPaneFunc(paneId, markIds);
  return markIndices.map((markIndex) => {
    if (markIndex < 0 || markIndex >= userPaneRows.length) {
      return -1;
    }
    const userPaneRow = userPaneRows[markIndex];
    return typeof userPaneRow === 'number' ? userPaneRow : -1;
  });
}

/**
 * Get user pane column indices for mark IDs in a pane.
 */
export function getUserPaneColumnIndicesForMarkIdsInPane(
  getValue: (path: string) => DataStoreValue,
  paneId: string,
  markIds: number[],
  getMarkIndicesForPaneFunc: (paneId: string, markIds: number[]) => number[]
): number[] {
  const userPaneCols = ArrayLikeUtils.toArray<number>(getValue(`PDMarks.${paneId}.Encodings.user_pane_col`));
  const markIndices = getMarkIndicesForPaneFunc(paneId, markIds);
  return markIndices.map((markIndex) => {
    if (markIndex < 0 || markIndex >= userPaneCols.length) {
      return -1;
    }
    const userPaneCol = userPaneCols[markIndex];
    return typeof userPaneCol === 'number' ? userPaneCol : -1;
  });
}

function getLabelEncodingCount(getValue: (path: string) => DataStoreValue, paneId: string): number {
  const labelXPath = ArrayLikeUtils.toArray<number>(getValue(`AnnexedData.LabelStore.PDMarks.${paneId}.Encodings.x`));
  const labelYPath = ArrayLikeUtils.toArray<number>(getValue(`AnnexedData.LabelStore.PDMarks.${paneId}.Encodings.y`));
  const labelWidthPath = ArrayLikeUtils.toArray<number>(getValue(`AnnexedData.LabelStore.PDMarks.${paneId}.Encodings.width`));
  const labelHeightPath = ArrayLikeUtils.toArray<number>(getValue(`AnnexedData.LabelStore.PDMarks.${paneId}.Encodings.height`));
  return Math.max(labelXPath.length, labelYPath.length, labelWidthPath.length, labelHeightPath.length);
}

/**
 * Get label mark IDs for a pane by matching tuple IDs between label store and marks.
 */
export function getLabelMarkIdsForPane(getValue: (path: string) => DataStoreValue, paneId: string): number[] {
  const labelTupleIds = ArrayLikeUtils.toArray<number>(getValue(`AnnexedData.LabelStore.PDMarks.${paneId}.Encodings.TupleId`));
  const markTupleIds = ArrayLikeUtils.toArray<number>(getValue(`PDMarks.${paneId}.Encodings.TupleId`));
  const markIds = ArrayLikeUtils.toArray<number>(getValue(`PDMarks.${paneId}.Encodings.MarkId`));

  if (markIds.length === 0) {
    return [];
  }

  if (labelTupleIds.length > 0 && markTupleIds.length > 0) {
    const tupleIdToMarkId = new Map<number, number>();
    const maxCount = Math.min(markTupleIds.length, markIds.length);
    for (let i = 0; i < maxCount; i++) {
      const tupleId = markTupleIds[i];
      const markId = markIds[i];
      if (typeof tupleId === 'number' && typeof markId === 'number') {
        tupleIdToMarkId.set(tupleId, markId);
      }
    }

    const result: number[] = [];
    for (const tupleId of labelTupleIds) {
      const markId = tupleIdToMarkId.get(tupleId);
      if (markId !== undefined) {
        result.push(markId);
      }
    }
    return result;
  }

  const labelEncodingCount = getLabelEncodingCount(getValue, paneId);
  if (labelEncodingCount > 0 && labelEncodingCount === markIds.length) {
    return markIds.slice(0, labelEncodingCount);
  }

  return [];
}

/**
 * Get the set of label mark IDs that are moving (present in both from and to states).
 */
export function getMoveLabelMarkIdSet(
  paneId: string,
  getLabelMarkIdsForPaneFunc: (paneId: string) => number[],
  targetGetLabelMarkIdsForPaneFunc: (paneId: string) => number[]
): Set<number> {
  const fromLabelMarkIds = getLabelMarkIdsForPaneFunc(paneId);
  const toLabelMarkIds = targetGetLabelMarkIdsForPaneFunc(paneId);
  if (fromLabelMarkIds.length === 0 || toLabelMarkIds.length === 0) {
    return new Set<number>();
  }

  const toSet = new Set<number>(toLabelMarkIds);
  const result = new Set<number>();
  for (const markId of fromLabelMarkIds) {
    if (toSet.has(markId)) {
      result.add(markId);
    }
  }
  return result;
}

/**
 * Check if the given attributes have nearly equal values between two states.
 * C++ Reference: DataStorePaneState::AttributesNearlyEqual()
 */
export function attributesNearlyEqual(
  getValue: (path: string) => DataStoreValue,
  otherGetValue: (path: string) => DataStoreValue,
  attributes: PaneStateAttribute[],
  epsilon: number,
  paneId = '',
  fromIndices?: number[] | null,
  otherIndices?: number[] | null
): boolean {
  for (const attribute of attributes) {
    const path = DataStorePaths.getKeyPathForAttribute(paneId, attribute);
    if (!path) {
      continue;
    }
    const thisValue = getValue(path);
    const otherValue = otherGetValue(path);
    if (!valuesNearlyEqual(thisValue, otherValue, attribute, epsilon, fromIndices, otherIndices)) {
      return false;
    }
  }
  return true;
}

/**
 * Check if mark attributes are nearly equal between two states for the given mark IDs.
 * C++ Reference: DataStorePaneState::MarksNearlyEqual()
 */
export function marksNearlyEqual(
  getValue: (path: string) => DataStoreValue,
  otherGetValue: (path: string) => DataStoreValue,
  paneId: string,
  markIds: number[],
  attributes: PaneStateAttribute[],
  epsilon: number
): boolean {
  if (markIds.length === 0) {
    return true;
  }
  const myIndices = getMarkIndicesForPane(getValue, paneId, markIds);
  const otherIndices = getMarkIndicesForPane(otherGetValue, paneId, markIds);
  return attributesNearlyEqual(getValue, otherGetValue, attributes, epsilon, paneId, myIndices, otherIndices);
}

/**
 * Check if label attributes are nearly equal between two states for the given mark IDs.
 * C++ Reference: DataStorePaneState::LabelsNearlyEqual()
 */
export function labelsNearlyEqual(
  getValue: (path: string) => DataStoreValue,
  otherGetValue: (path: string) => DataStoreValue,
  paneId: string,
  markIds: number[],
  attributes: PaneStateAttribute[],
  epsilon: number
): boolean {
  if (markIds.length === 0) {
    return true;
  }
  const myIndices = getLabelIndicesForPane(getValue, paneId, markIds, (id: string) => getLabelMarkIdsForPane(getValue, id));
  const otherIndices = getLabelIndicesForPane(otherGetValue, paneId, markIds, (id: string) => getLabelMarkIdsForPane(otherGetValue, id));
  return attributesNearlyEqual(getValue, otherGetValue, attributes, epsilon, paneId, myIndices, otherIndices);
}
